import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { redeemGuestCv } from './api';
import { GuestRedeemResponse } from './api-types';
import {
  GrantErrorStage,
  reportGrantOutcome,
  stateFromError
} from './grantErrors';
import {
  cacheRecoveredSession,
  clearCachedSession,
  getCachedSession,
  getPendingRedeem,
  setCachedSession,
  trackPendingRedeem
} from './guestSessionCache';
import {
  GuestRedeemState,
  GuestSession,
  GuestSessionContext
} from './GuestSessionContext';

/**
 * Reads the magic-link token from `?t=`, redeems it for an in-memory session token, and
 * exposes the redeem outcome to the guest subtree. The token is never persisted: on a
 * page refresh this re-runs from the token still in the URL — that re-redeem IS the
 * resume mechanism (the server returns the saved CV + progress), so no localStorage.
 */
export const GuestSessionProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams] = useSearchParams();
  const linkToken = searchParams.get('t');

  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [redeemData, setRedeemData] = useState<GuestRedeemResponse | null>(
    null
  );
  const [errorState, setErrorState] = useState<GuestRedeemState | null>(null);
  const attemptedRef = useRef(false);
  // Single-flight guard for re-redeem: concurrent failed writes share one recovery.
  const recoveringRef = useRef<Promise<string | null> | null>(null);
  // Mirrors sessionToken for getSessionToken(): async flows need the freshest token
  // synchronously, before React has re-rendered with the post-recovery state.
  const sessionTokenRef = useRef<string | null>(null);

  useEffect(() => {
    // Redeem once per mount. The ref guard stops React 18 StrictMode's double-invoke
    // from minting two sessions (the second would invalidate the first server-side).
    if (attemptedRef.current) return;
    attemptedRef.current = true;

    if (!linkToken) {
      // No token in the URL — typically a mid-session refresh (we keep the token only in
      // memory). Distinct from 'invalid' so we can reassure the user their data is safe.
      setErrorState('missing-token');
      return;
    }

    // Remount-safe: reuse a session already minted for this token in this page load, so a
    // spurious remount can't rotate the session out from under an in-flight autosave.
    const cached = getCachedSession(linkToken);
    if (cached) {
      sessionTokenRef.current = cached.sessionToken;
      setSessionToken(cached.sessionToken);
      setRedeemData(cached);
      return;
    }

    // The cache is only written on success, so a remount DURING the first redeem would
    // previously fire a second POST /redeem — invalidating the session the first request
    // was about to return. Subscribe to the in-flight request instead.
    const pending = getPendingRedeem(linkToken);
    const request = pending ?? redeemGuestCv(linkToken);
    if (!pending) {
      trackPendingRedeem(linkToken, request);
    }

    // These handlers deliberately run even if THIS instance has unmounted by the time
    // the request settles: the cache write and the single outcome report must happen
    // exactly once regardless of which provider instance survives a remount. The
    // setState calls are safe no-ops on an unmounted component (React 18) — do not add
    // an unmount guard here, it would drop the cache write/report when the initiating
    // instance is the one that unmounted.
    request.then(
      data => {
        setCachedSession(linkToken, data);
        sessionTokenRef.current = data.sessionToken;
        setSessionToken(data.sessionToken);
        setRedeemData(data);
      },
      (error: unknown) => {
        // Only the caller that fired the request reports the outcome — a subscriber
        // reporting too would double-count a single failed redeem in Error Tracking.
        if (!pending) {
          reportGrantOutcome('redeem', error);
        }
        setErrorState(stateFromError(error));
      }
    );
  }, [linkToken]);

  const markSubmitted = useCallback(() => {
    clearCachedSession(); // grant is terminal — a remount must not re-activate a consumed session
    setErrorState('submitted');
  }, []);

  // Re-redeem the still-valid link token for a fresh session. Refreshes ONLY the session
  // token (not redeemData) so the in-progress form is never re-seeded/reset. A terminal
  // failure (grant expired/submitted/invalid) flips to the matching screen and returns null.
  const recoverSession = useCallback((): Promise<string | null> => {
    if (recoveringRef.current) return recoveringRef.current; // single-flight
    if (!linkToken) {
      setErrorState('missing-token');
      return Promise.resolve(null);
    }
    const attempt = (async (): Promise<string | null> => {
      // Share a redeem already in flight (e.g. a remounted provider's initial redeem)
      // instead of stacking another rotation on top of it.
      const pending = getPendingRedeem(linkToken);
      const request = pending ?? redeemGuestCv(linkToken);
      if (!pending) {
        trackPendingRedeem(linkToken, request);
      }
      try {
        const data = await request;
        // Refresh only the live session token in the cache (keep the original seed data), so a
        // later remount restores THIS session instead of minting yet another one.
        cacheRecoveredSession(linkToken, data);
        sessionTokenRef.current = data.sessionToken;
        setSessionToken(data.sessionToken);
        return data.sessionToken;
      } catch (error) {
        // Same single-reporter rule as the mount redeem: the initiator reports.
        if (!pending) {
          reportGrantOutcome('recovery', error);
        }
        clearCachedSession(); // grant dead/unreachable — don't let a remount reuse a stale session
        setErrorState(stateFromError(error));
        return null;
      } finally {
        recoveringRef.current = null;
      }
    })();
    recoveringRef.current = attempt;
    return attempt;
  }, [linkToken]);

  const getSessionToken = useCallback(
    (): string | null => sessionTokenRef.current,
    []
  );

  // A write hit a dead grant (submitted elsewhere / window over / revoked): report the
  // outcome, drop the cached session so a remount can't resurrect it, and swap to the
  // matching terminal screen. already-submitted lands on the success screen.
  const routeGrantError = useCallback(
    (error: unknown, stage: GrantErrorStage) => {
      reportGrantOutcome(stage, error);
      clearCachedSession();
      setErrorState(stateFromError(error));
    },
    []
  );

  const value = useMemo<GuestSession>(() => {
    let redeemState: GuestRedeemState = 'loading';
    if (errorState) {
      redeemState = errorState;
    } else if (sessionToken) {
      redeemState = 'active';
    }
    return {
      sessionToken,
      redeemState,
      redeemData,
      markSubmitted,
      getSessionToken,
      recoverSession,
      routeGrantError
    };
  }, [
    sessionToken,
    redeemData,
    errorState,
    markSubmitted,
    getSessionToken,
    recoverSession,
    routeGrantError
  ]);

  return (
    <GuestSessionContext.Provider value={value}>
      {children}
    </GuestSessionContext.Provider>
  );
};
